// LinkUps data transformation utilities
// Maps Supabase LinkUps data to frontend component expectations

import { formatMemberCount } from './crewData';

const toCount = value => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

export const resolveLinkupAttendeeCount = linkup => {
  if (!linkup) return 0;

  const candidateCount = [
    linkup.members_count,
    linkup.memberCount,
    linkup.attendeeCount,
    linkup.totalGoing,
    linkup.total_going,
    linkup.attendee_count,
    linkup.membersCount,
    linkup.members,
  ]
    .map(toCount)
    .find(count => count !== null);

  if (candidateCount !== undefined) {
    return candidateCount;
  }

  if (Array.isArray(linkup.attendees)) {
    return linkup.attendees.length;
  }

  if (Array.isArray(linkup.memberships)) {
    return linkup.memberships.length;
  }

  return 0;
};

/**
 * Calculate total engagement for a LinkUp
 * This could be used for sorting by activity
 */
export const calculateLinkupEngagement = linkup => {
  const members = resolveLinkupAttendeeCount(linkup);
  const daysSinceCreated = linkup.created_at
    ? Math.ceil((new Date() - new Date(linkup.created_at)) / (1000 * 60 * 60 * 24))
    : 0;

  // Simple engagement score: members / days (with minimum of 1 day)
  return members / Math.max(daysSinceCreated, 1);
};

/**
 * Format member count for display
 * Reuses the crew formatting logic
 */
export const formatLinkupMemberCount = count => {
  return formatMemberCount(count);
};

/**
 * Generate status string for LinkUp cards
 * Shows category, member count, and availability
 */
export const formatLinkupStatus = (linkup, includeCategory = true, includeAvailability = true) => {
  const category = linkup.linkUp_category || 'General';
  const memberCount = resolveLinkupAttendeeCount(linkup);
  const fanLimit = linkup.linkUp_fan_limit;

  let status = '';

  if (includeCategory) {
    status = category;
  }

  if (includeAvailability) {
    const memberText = memberCount === 1 ? 'fan' : 'fans';
    if (fanLimit && fanLimit > 0) {
      const available = Math.max(fanLimit - memberCount, 0);
      if (available > 0) {
        status += status ? ` • ${available} spots left` : `${available} spots left`;
      } else {
        status += status ? ' • Full' : 'Full';
      }
    } else {
      status += status
        ? ` • ${memberCount} ${memberText} joined`
        : `${memberCount} ${memberText} joined`;
    }
  }

  return status || 'Active LinkUp';
};

/**
 * Format LinkUp date for display
 */
export const formatLinkupDate = dateString => {
  if (!dateString) return 'TBD';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Past event';
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays < 7) {
    return `In ${diffDays} days`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
};

/**
 * Get LinkUp type display information
 */
export const getLinkupTypeInfo = linkupType => {
  const types = {
    public: {
      label: 'Public',
      description: 'Anyone can find and join this LinkUp.',
      color: '#10B981', // green
    },
    private: {
      label: 'Private',
      description: 'Only people with the link can join.',
      color: '#F59E0B', // amber
    },
    limited: {
      label: 'Limited',
      description: 'Public until the fan limit is reached.',
      color: '#3B82F6', // blue
    },
  };

  return types[linkupType] || types.public;
};

/**
 * Check if LinkUp is available to join
 */
export const isLinkupAvailable = linkup => {
  if (linkup.linkUp_status !== 'active') return false;

  const fanLimit = linkup.linkUp_fan_limit;
  const memberCount = resolveLinkupAttendeeCount(linkup);

  if (fanLimit && fanLimit > 0) {
    return memberCount < fanLimit;
  }

  return true;
};

export const deriveCityFromAddress = address => {
  if (typeof address !== 'string') {
    return '';
  }

  const addressParts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (addressParts.length >= 4) {
    return addressParts[addressParts.length - 3];
  }

  if (addressParts.length === 3) {
    return addressParts[1];
  }

  if (addressParts.length === 2) {
    return addressParts[0];
  }

  return '';
};

const firstNonEmptyString = values =>
  values.find(value => typeof value === 'string' && value.trim())?.trim() || '';

export const resolveLinkupImage = linkup =>
  firstNonEmptyString([
    linkup?.image,
    linkup?.linkUp_header_image,
    linkup?.headerImage,
    linkup?.imageUrl,
    linkup?.image_url,
    linkup?.event_image_url,
    linkup?.eventImageUrl,
    linkup?.event?.image,
    linkup?.event?.imageUrl,
    linkup?.event?.image_url,
    linkup?.crew_image,
    linkup?.crewImage,
    linkup?.crew_header_image,
    linkup?.source_crew_image,
    linkup?.crew?.image,
    linkup?.crew?.imageUrl,
    linkup?.crew?.crew_header_image,
    linkup?.attraction_image,
    linkup?.attractionImage,
    linkup?.attraction_image_url,
    linkup?.attraction?.image,
    linkup?.attraction?.image_url,
  ]);

/**
 * Transform Supabase LinkUp data to frontend format
 */
export const transformLinkupData = (supabaseLinkup, options = {}) => {
  const { includeFullDetails = false, includeCrewInfo = false, includeEventInfo = false } = options;

  if (!supabaseLinkup) return null;
  const memberCount = resolveLinkupAttendeeCount(supabaseLinkup);

  // Base data that's always included
  const baseData = {
    id: supabaseLinkup.id,
    name: supabaseLinkup.linkUp_name,
    description: supabaseLinkup.linkUp_description || '',
    image: resolveLinkupImage(supabaseLinkup),
    category: supabaseLinkup.linkUp_category || 'General',
    status: formatLinkupStatus(supabaseLinkup, true, true),
    type: supabaseLinkup.linkUp_type || 'public',
    typeInfo: getLinkupTypeInfo(supabaseLinkup.linkUp_type),

    // Location info
    address: supabaseLinkup.linkUp_address || '',
    locationDescription: supabaseLinkup.location_description || '',

    // Member info
    memberCount,
    memberCountFormatted: formatLinkupMemberCount(memberCount),
    fanLimit: supabaseLinkup.linkUp_fan_limit || null,
    isAvailable: isLinkupAvailable(supabaseLinkup),

    // Date info
    date: supabaseLinkup.linkUp_date,
    dateFormatted: formatLinkupDate(supabaseLinkup.linkUp_date),
    createdAt: supabaseLinkup.created_at,

    // Creator info
    createdBy: supabaseLinkup.created_by,

    // Status
    linkUpStatus: supabaseLinkup.linkUp_status || 'active',

    // Engagement metrics
    engagement: calculateLinkupEngagement(supabaseLinkup),
  };

  // Additional data based on options
  if (includeFullDetails) {
    baseData.inviteLink = supabaseLinkup.invite_link;
    baseData.eventId = supabaseLinkup.event_id;
    baseData.crewId = supabaseLinkup.crew_id;
  }

  if (includeCrewInfo && supabaseLinkup.crew_id) {
    baseData.crew = {
      id: supabaseLinkup.crew_id,
      // Additional crew info would be populated by joining with Crews table
      name: supabaseLinkup.crew_name || 'Unknown Crew',
      category: supabaseLinkup.crew_category || 'General',
    };
  }

  if (includeEventInfo && supabaseLinkup.event_id) {
    baseData.event = {
      id: supabaseLinkup.event_id,
      // Additional event info would be populated by joining with Events table
      name: supabaseLinkup.event_name || 'Unknown Event',
    };
  }

  return baseData;
};

/**
 * Transform Supabase LinkUp data to LinkUpCard format
 * This is specifically for the LinkUpCard component in CrewsPage
 */
export const transformLinkupForCard = supabaseLinkup => {
  if (!supabaseLinkup) return null;

  const memberCount = resolveLinkupAttendeeCount(supabaseLinkup);

  return {
    id: supabaseLinkup.id,
    title: supabaseLinkup.linkUp_name || 'LinkUp Event',
    location: supabaseLinkup.linkUp_address || 'Location TBD',
    city:
      firstNonEmptyString([
        supabaseLinkup.location_description,
        deriveCityFromAddress(supabaseLinkup.linkUp_address),
        supabaseLinkup.linkUp_address,
      ]) || 'City TBD',
    description: supabaseLinkup.linkUp_description || '',
    eventName: supabaseLinkup.event_name || 'Event',
    date: supabaseLinkup.linkUp_date
      ? new Date(supabaseLinkup.linkUp_date).toLocaleDateString()
      : 'Date TBD',
    time: supabaseLinkup.time || 'Time TBD', // Use actual database time field, not converted date
    crewId: supabaseLinkup.crew_id,
    crew: supabaseLinkup.crew_name || '', // Add crew name for filtering
    category: supabaseLinkup.linkUp_category || 'General', // Add category for filtering
    image: resolveLinkupImage(supabaseLinkup),
    attendees: Array.isArray(supabaseLinkup.attendees) ? supabaseLinkup.attendees : [],
    attendeeCount: memberCount,
    members_count: memberCount,
    totalGoing: memberCount,
    status: 'Join LinkUp', // Default status for button text
    isGeneric: false, // This is real data, not a generic placeholder
  };
};

/**
 * Transform array of LinkUps for LinkUpCard components
 */
export const transformLinkupsForCards = linkups => {
  if (!Array.isArray(linkups)) return [];

  return linkups
    .filter(linkup => linkup && linkup.id) // Filter out invalid entries
    .map(linkup => transformLinkupForCard(linkup));
};

/**
 * Transform array of LinkUps
 */
export const transformLinkupList = (linkups, options = {}) => {
  if (!Array.isArray(linkups)) return [];

  return linkups.map(linkup => transformLinkupData(linkup, options)).filter(Boolean); // Remove any null/undefined items
};

/**
 * Sort LinkUps by various criteria
 */
export const sortLinkups = (linkups, sortBy = 'recent') => {
  if (!Array.isArray(linkups)) return [];

  const sorted = [...linkups];

  switch (sortBy) {
    case 'recent':
      return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    case 'members':
      return sorted.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));

    case 'engagement':
      return sorted.sort((a, b) => (b.engagement || 0) - (a.engagement || 0));

    case 'date':
      return sorted.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateA - dateB;
      });

    case 'name':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    case 'category':
      return sorted.sort((a, b) => (a.category || '').localeCompare(b.category || ''));

    default:
      return sorted;
  }
};

/**
 * Filter LinkUps by category
 */
export const filterLinkupsByCategory = (linkups, category) => {
  if (!Array.isArray(linkups) || !category || category === 'all') return linkups;

  return linkups.filter(linkup => (linkup.category || '').toLowerCase() === category.toLowerCase());
};

/**
 * Filter LinkUps by availability
 */
export const filterLinkupsByAvailability = (linkups, availableOnly = true) => {
  if (!Array.isArray(linkups)) return linkups;

  return linkups.filter(linkup => (availableOnly ? linkup.isAvailable : true));
};

/**
 * Filter LinkUps by type
 */
export const filterLinkupsByType = (linkups, type) => {
  if (!Array.isArray(linkups) || !type || type === 'all') return linkups;

  return linkups.filter(linkup => linkup.type === type);
};

/**
 * Search LinkUps by name, description, or location
 */
export const searchLinkups = (linkups, query) => {
  if (!Array.isArray(linkups) || !query || !query.trim()) return linkups;

  const searchTerm = query.toLowerCase().trim();

  return linkups.filter(linkup => {
    const name = (linkup.name || '').toLowerCase();
    const description = (linkup.description || '').toLowerCase();
    const location = (linkup.locationDescription || linkup.address || '').toLowerCase();
    const category = (linkup.category || '').toLowerCase();

    return (
      name.includes(searchTerm) ||
      description.includes(searchTerm) ||
      location.includes(searchTerm) ||
      category.includes(searchTerm)
    );
  });
};

/**
 * Get LinkUp statistics for dashboard
 */
export const getLinkupStats = linkups => {
  if (!Array.isArray(linkups)) {
    return {
      total: 0,
      active: 0,
      totalMembers: 0,
      byCategory: {},
      byType: {},
    };
  }

  const stats = {
    total: linkups.length,
    active: linkups.filter(l => l.linkUpStatus === 'active').length,
    totalMembers: linkups.reduce((sum, l) => sum + (l.memberCount || 0), 0),
    byCategory: {},
    byType: {},
  };

  linkups.forEach(linkup => {
    // Category stats
    const category = linkup.category || 'General';
    stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

    // Type stats
    const type = linkup.type || 'public';
    stats.byType[type] = (stats.byType[type] || 0) + 1;
  });

  return stats;
};

export default {
  transformLinkupData,
  transformLinkupList,
  transformLinkupForCard,
  transformLinkupsForCards,
  sortLinkups,
  filterLinkupsByCategory,
  filterLinkupsByAvailability,
  filterLinkupsByType,
  searchLinkups,
  getLinkupStats,
  formatLinkupStatus,
  formatLinkupDate,
  formatLinkupMemberCount,
  resolveLinkupAttendeeCount,
  getLinkupTypeInfo,
  isLinkupAvailable,
  calculateLinkupEngagement,
};
